/**
 * PolicyTemplateBrowser
 *
 * Read-only browser for governance policy templates. Filters out SOP-category
 * templates so only true policy documents are shown. Supports preview toggle
 * and clipboard copy of raw markdown content.
 *
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { BrowserLayout } from './BrowserLayout';

// ============================================================================
// TYPES
// ============================================================================

interface PolicyTemplate {
  template_id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  template_content: string;
  compliance_frameworks: string[];
}

interface ApiResponse {
  templates?: PolicyTemplate[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const PolicyTemplateBrowser: React.FC = () => {
  const { authFetch } = useApiClient();

  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch('/api/governance/templates/policies');
        if (!response.ok) {
          throw new Error(`Failed to load policy templates (HTTP ${response.status})`);
        }
        const data: ApiResponse | PolicyTemplate[] = await response.json();
        const raw: PolicyTemplate[] = Array.isArray(data)
          ? data
          : (data as ApiResponse).templates ?? [];
        const filtered = raw.filter((t) => t.category !== 'sop');
        if (!cancelled) {
          setTemplates(filtered);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unexpected error loading templates.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const togglePreview = (id: string) => {
    setPreviewOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = async (template: PolicyTemplate) => {
    try {
      await navigator.clipboard.writeText(template.template_content);
      setCopiedId(template.template_id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard write failed — silently ignore
    }
  };

  // ---- Render states -------------------------------------------------------

  if (loading) {
    return (
      <BrowserLayout
        title="Policy Templates"
        description="Governance policy documents with regulatory citations and fill-in-the-blank variables for ML governance, data quality, FAIR implementation, privacy, and AI documentation."
      >
        <div className="browser-loading">
          <div className="spinner" />
          Loading policy templates…
        </div>
      </BrowserLayout>
    );
  }

  if (error) {
    return (
      <BrowserLayout
        title="Policy Templates"
        description="Governance policy documents with regulatory citations and fill-in-the-blank variables for ML governance, data quality, FAIR implementation, privacy, and AI documentation."
      >
        <div className="browser-error">
          <div className="error-icon">⚠</div>
          <p>{error}</p>
        </div>
      </BrowserLayout>
    );
  }

  // ---- Main render ---------------------------------------------------------

  return (
    <BrowserLayout
      title="Policy Templates"
      description="Governance policy documents with regulatory citations and fill-in-the-blank variables for ML governance, data quality, FAIR implementation, privacy, and AI documentation."
    >
      <div className="template-card-grid">
        {templates.map((template) => {
          const isOpen = previewOpen[template.template_id] ?? false;
          const isCopied = copiedId === template.template_id;
          const frameworks: string[] = Array.isArray(template.compliance_frameworks)
            ? template.compliance_frameworks
            : [];

          return (
            <div key={template.template_id} className="template-card">
              <h3>{template.name}</h3>
              <p className="template-card-description">{template.description}</p>

              {frameworks.length > 0 && (
                <div className="template-card-badges">
                  {frameworks.map((fw) => (
                    <span key={fw} className="badge badge-blue">
                      {fw}
                    </span>
                  ))}
                </div>
              )}

              <div className="template-card-actions">
                <button
                  type="button"
                  className="btn-sm"
                  onClick={() => togglePreview(template.template_id)}
                >
                  {isOpen ? 'Hide Preview' : 'Preview'}
                </button>
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={() => void handleCopy(template)}
                >
                  {isCopied ? 'Copied!' : 'Copy Markdown'}
                </button>
              </div>

              {isOpen && (
                <div
                  className="template-preview"
                  // Render as plain whitespace-preserved text to avoid XSS
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {template.template_content}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </BrowserLayout>
  );
};

export default PolicyTemplateBrowser;
